<template>
    <Head title="Coaching setup" />
    <GuidedSetupPage :setup="setup" :chat-input="chatInput" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
/**
 * Calendar OAuth returns with ?setup= to reopen the relevant step. Remove the parameter after handling it
 * so refreshes do not replay that navigation.
 */
import { Head } from "@inertiajs/vue3";
import type { GlobalProperties } from "~vue/appTypes";
import GuidedSetupPage from "~vue/ProfileSetup/GuidedSetupPage.vue";
import type { ChatInputProps, SetupState, SetupStepId } from "~vue/ProfileSetup/types";
import { SETUP_STEP_ORDER, useProfileSetup } from "~vue/ProfileSetup/useProfileSetup";
import type { PatternEntry } from "~vue/utils/pii/keywordClassifier";
import { computed, inject, onMounted } from "vue";

const props = defineProps<{
    profile_setup: SetupState;
    event_context: Record<string, string>;
    default_chat_redirect_url: string;
    document_upload_enabled: boolean;
    pii_patterns: PatternEntry[] | null;
    better_voice_composer_enabled: boolean;
    has_workiq_connected: boolean;
}>();
const { profile_setup, event_context } = props;

const chatInput = computed<ChatInputProps>(() => ({
    default_chat_redirect_url: props.default_chat_redirect_url,
    document_upload_enabled: props.document_upload_enabled,
    pii_patterns: props.pii_patterns,
    better_voice_composer_enabled: props.better_voice_composer_enabled,
    has_workiq_connected: props.has_workiq_connected,
}));

const { $setEventContext } = inject<GlobalProperties>("globalProperties")!;

const setup = useProfileSetup(profile_setup);

function requestedStep(): SetupStepId | null {
    const url = new URL(globalThis.location.href);
    const requested = url.searchParams.get("setup");
    if (requested === null) {
        return null;
    }
    url.searchParams.delete("setup");
    globalThis.history.replaceState(globalThis.history.state, "", url.toString());
    return SETUP_STEP_ORDER.includes(requested as SetupStepId) ? (requested as SetupStepId) : null;
}

onMounted(() => {
    $setEventContext(event_context);
    const step = requestedStep() ?? setup.nextUnfinished();
    if (step !== null) {
        setup.openStep(step);
    }
});
</script>
